import html2canvas from 'html2canvas';
import QRCode from 'qrcode';

export const WHATSAPP_STATUS_SIZE = { width: 1080, height: 1920 };
export const HIRING_REGISTER_LINK = 'https://hiring.paytiemp.com/signup';
export const HIRING_REGISTER_DISPLAY = 'hiring.paytiemp.com';

const POSTER_FONT_URL =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@600;700;800&display=swap';

/** System font — html2canvas renders vertical centering reliably (web-font metrics skew pills). */
const BADGE_FONT = 'Arial, Helvetica, sans-serif';

const C = {
  bg: '#F4F7FB',
  bgSoft: '#EEF3FA',
  white: '#FFFFFF',
  ink: '#0F172A',
  inkSoft: '#334155',
  muted: '#64748B',
  accent: '#2563EB',
  accentSoft: '#DBEAFE',
  accentWarm: '#F97316',
  accentWarmSoft: '#FFEDD5',
  line: '#E2E8F0',
  success: '#059669',
  black: '#000000'
};

let fontsPromise = null;

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function normalizeSkills(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function formatInr(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num) || num <= 0) return '';
  return `₹${Math.round(num).toLocaleString('en-IN')}`;
}

function formatSalaryRange(budgetFrom, budgetTo) {
  const from = formatInr(budgetFrom);
  const to = formatInr(budgetTo);
  if (from && to) return `${from} – ${to} PA`;
  if (from) return `${from}+ PA`;
  if (to) return `Up to ${to} PA`;
  return 'As per experience';
}

function formatExperience(from, to) {
  const a = Number(from);
  const b = Number(to);
  if (Number.isFinite(a) && Number.isFinite(b) && a > 0 && b > 0) {
    if (a === b) return `${a} Year${a === 1 ? '' : 's'}`;
    return `${a} – ${b} Years`;
  }
  if (Number.isFinite(a) && a > 0) return `${a}+ Years`;
  if (Number.isFinite(b) && b > 0) return `Up to ${b} Years`;
  return 'Fresher to Experienced';
}

function formatWeeklyWorkingDays(value) {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return `${n} Days / Week`;
  const text = String(value || '').trim();
  return text || 'As per policy';
}

function formatEmploymentType(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('-');
}

function truncate(text, max) {
  const s = String(text || '').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function displayWebsite(url) {
  const raw = String(url || HIRING_REGISTER_DISPLAY).trim();
  return raw.replace(/^https?:\/\//i, '').replace(/\/$/, '') || HIRING_REGISTER_DISPLAY;
}

export function buildRegisterUrl(jobId) {
  const base = HIRING_REGISTER_LINK.replace(/\/$/, '');
  if (jobId) return `${base}?job=${encodeURIComponent(String(jobId))}`;
  return base;
}

/**
 * html2canvas-safe pill: vertical center via padding-top = (height - fontSize) / 2.
 * Avoids flex/grid/SVG text — all of which misalign in canvas capture.
 */
function htmlPill(text, opts) {
  const {
    width = null,
    height,
    fontSize,
    bg,
    color,
    border = null,
    uppercase = true,
    inline = false,
    maxWidth = null
  } = opts;
  const label = uppercase ? String(text).trim().toUpperCase() : String(text).trim();
  const padTop = Math.max(0, Math.round((height - fontSize) / 2) - 2);
  const radius = Math.round(height / 2);
  const widthCss = width ? `width:${width}px;` : '';
  const display = inline ? 'inline-block' : 'block';
  const margin = inline ? '' : 'margin-left:auto;margin-right:auto;';
  const maxWidthCss = maxWidth ? `max-width:${maxWidth}px;white-space:normal;` : 'white-space:nowrap;';
  const borderCss = border ? `border:2px solid ${border};` : 'border:none;';

  return `<div style="${display};${widthCss}${margin}height:${height}px;${borderCss}background:${bg};border-radius:${radius}px;text-align:center;padding:${padTop}px 28px 0;font-family:${BADGE_FONT};font-size:${fontSize}px;font-weight:800;color:${color};line-height:1;${maxWidthCss}box-sizing:border-box;overflow:hidden;">${escapeHtml(label)}</div>`;
}

function htmlChip(text) {
  const height = 40;
  const fontSize = 17;
  const padTop = Math.round((height - fontSize) / 2) - 1;
  return `<span style="display:inline-block;height:${height}px;padding:${padTop}px 18px 0;border-radius:20px;background:#DBEAFE;border:2px solid #93C5FD;font-family:${BADGE_FONT};font-size:${fontSize}px;font-weight:700;color:#1E40AF;line-height:1;white-space:nowrap;box-sizing:border-box;">${escapeHtml(text)}</span>`;
}

function buildLogoBlock(branding, absoluteLogoUrl) {
  const companyName = branding.appName || 'Company';
  if (absoluteLogoUrl) {
    return `
      <div class="brand-row">
        <div class="brand-logo-wrap">
          <img src="${escapeHtml(absoluteLogoUrl)}" alt="" class="brand-logo" crossorigin="anonymous" />
        </div>
        <div class="brand-copy">
          <div class="brand-name">${escapeHtml(companyName)}</div>
          ${branding.tagline ? `<div class="brand-tagline">${escapeHtml(truncate(branding.tagline, 56))}</div>` : ''}
        </div>
      </div>
    `;
  }
  const initials = companyName
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return `
    <div class="brand-row">
      <div class="brand-logo-wrap brand-logo-fallback">${escapeHtml(initials || 'CO')}</div>
      <div class="brand-copy">
        <div class="brand-name">${escapeHtml(companyName)}</div>
        ${branding.tagline ? `<div class="brand-tagline">${escapeHtml(truncate(branding.tagline, 56))}</div>` : ''}
      </div>
    </div>
  `;
}

function infoTile(label, value, accent = 'blue') {
  if (!value) return '';
  return `
    <div class="info-tile info-tile--${accent}">
      <div class="info-label">${escapeHtml(label)}</div>
      <div class="info-value">${escapeHtml(value)}</div>
    </div>
  `;
}

function contactBlock(icon, label, value) {
  return `
    <div class="contact-block">
      <div class="contact-block-icon">${icon}</div>
      <div class="contact-block-label">${escapeHtml(label)}</div>
      <div class="contact-block-value">${escapeHtml(value || '—')}</div>
    </div>
  `;
}

export function buildJobPosterHtml({ job, branding, absoluteLogoUrl, qrDataUrl }) {
  const title = job.title || 'Open Position';
  const location = job.location || 'Multiple Locations';
  const positions = Number(job.numberOfPositions) || 1;
  const skills = normalizeSkills(job.mustHaveSkills).slice(0, 4);
  const techSkills = normalizeSkills(job.mustHaveTechnicalSkills).slice(0, 3);
  const allSkills = [...skills, ...techSkills].slice(0, 5);
  const salary = formatSalaryRange(job.budgetFrom, job.budgetTo);
  const experience = formatExperience(job.experienceFrom, job.experienceTo);
  const employmentType = formatEmploymentType(job.employmentType) || 'Full-Time';
  const industry = truncate(branding.natureOfBusiness || branding.tagline || '', 36);
  const noticePeriod = String(job.noticePeriod || '').trim() || 'Immediate / Negotiable';
  const weeklyWorkingDays = formatWeeklyWorkingDays(job.weeklyWorkingDays);
  const eduList =
    Array.isArray(job.educationalQualifications) && job.educationalQualifications.length
      ? job.educationalQualifications
      : job.educationalQualification
        ? [job.educationalQualification]
        : [];
  const education = eduList.length
    ? truncate(eduList.join(' · '), 72)
    : 'As per role requirement';

  const contactPhone = String(branding.phone || '').trim();
  const contactEmail = String(branding.email || '').trim();
  const contactWebsite = displayWebsite(branding.website || HIRING_REGISTER_DISPLAY);

  const infoTiles = [
    infoTile('Location', location, 'blue'),
    infoTile('Experience', experience, 'blue'),
    infoTile('CTC / Package', salary, 'warm'),
    infoTile('Employment', employmentType, 'warm'),
    infoTile('Qualification', education, 'blue'),
    infoTile('Notice Period', noticePeriod, 'blue'),
    infoTile('Working Days', weeklyWorkingDays, 'warm')
  ]
    .filter(Boolean)
    .join('');

  const skillsHtml = allSkills.length
    ? `
      <div class="section">
        <div class="section-title">Key Skills</div>
        <div class="chips">${allSkills.map(s => htmlChip(s)).join('')}</div>
      </div>
    `
    : '';

  const contactBlocks = [
    contactBlock('📱', 'Mobile', contactPhone),
    contactBlock('✉️', 'Email', contactEmail),
    contactBlock('🌐', 'Website', contactWebsite)
  ].join('');

  const contactCols = 3;

  const hiringBadge = htmlPill('We Are Hiring', {
    width: 520,
    height: 80,
    fontSize: 34,
    bg: '#2563EB',
    color: '#FFFFFF'
  });

  const industryPill = industry
    ? htmlPill(industry, {
        height: 48,
        fontSize: 18,
        bg: '#FFEDD5',
        color: '#9A3412',
        border: '#FDBA74',
        inline: true,
        maxWidth: 880
      })
    : '';

  const applyPill = htmlPill('Apply Now', {
    width: 240,
    height: 52,
    fontSize: 24,
    bg: '#059669',
    color: '#FFFFFF',
    inline: true
  });

  return `
<div id="job-poster-root" class="poster">
<link rel="stylesheet" href="${POSTER_FONT_URL}" />
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }

  .poster {
    width: ${WHATSAPP_STATUS_SIZE.width}px;
    height: ${WHATSAPP_STATUS_SIZE.height}px;
    font-family: 'Inter', ${BADGE_FONT};
    background: linear-gradient(180deg, ${C.bg} 0%, ${C.white} 45%, ${C.bgSoft} 100%);
    color: ${C.ink};
    overflow: hidden;
    position: relative;
  }

  .shell {
    position: relative;
    z-index: 1;
    height: 100%;
    padding: 40px 44px 36px;
  }

  .top-card {
    background: ${C.white};
    border: 1px solid ${C.line};
    border-radius: 22px;
    padding: 26px 28px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  }

  .brand-row {
    display: block;
  }

  .brand-row-inner {
    display: table;
    width: 100%;
  }

  .brand-logo-cell,
  .brand-copy-cell {
    display: table-cell;
    vertical-align: middle;
  }

  .brand-logo-cell {
    width: 96px;
    padding-right: 20px;
  }

  .brand-logo-wrap,
  .brand-logo-fallback {
    width: 88px;
    height: 88px;
    border-radius: 16px;
    background: ${C.bgSoft};
    border: 1px solid ${C.line};
    text-align: center;
    padding-top: 24px;
    font-family: 'Plus Jakarta Sans', ${BADGE_FONT};
    font-size: 32px;
    font-weight: 800;
    color: ${C.accent};
  }

  .brand-logo {
    width: 88px;
    height: 88px;
    object-fit: contain;
    border-radius: 16px;
    display: block;
  }

  .brand-name {
    font-family: 'Plus Jakarta Sans', ${BADGE_FONT};
    font-size: 36px;
    font-weight: 800;
    line-height: 1.15;
    color: ${C.ink};
  }

  .brand-tagline {
    margin-top: 4px;
    font-size: 20px;
    font-weight: 500;
    color: ${C.muted};
  }

  .industry-wrap {
    margin-top: 18px;
    text-align: center;
  }

  .badge-wrap {
    margin: 24px 0 20px;
    text-align: center;
  }

  .title-card {
    background: ${C.white};
    border: 1px solid ${C.line};
    border-radius: 20px;
    padding: 26px 28px 22px;
    text-align: center;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.05);
  }

  .job-title {
    font-family: 'Plus Jakarta Sans', ${BADGE_FONT};
    font-size: 50px;
    font-weight: 800;
    line-height: 1.12;
    color: ${C.ink};
    word-break: break-word;
  }

  .job-meta {
    margin-top: 10px;
    font-size: 22px;
    font-weight: 700;
    color: ${C.accentWarm};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .section {
    margin-top: 20px;
  }

  .section-title {
    font-family: 'Plus Jakarta Sans', ${BADGE_FONT};
    font-size: 20px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: ${C.accent};
    margin-bottom: 12px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .info-tile {
    background: ${C.white};
    border: 1px solid ${C.line};
    border-radius: 14px;
    padding: 14px 16px;
    min-height: 80px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  }

  .info-tile--blue { border-left: 4px solid ${C.accent}; }
  .info-tile--warm { border-left: 4px solid ${C.accentWarm}; }

  .info-label {
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${C.muted};
    margin-bottom: 4px;
  }

  .info-value {
    font-size: 22px;
    font-weight: 700;
    color: ${C.ink};
    line-height: 1.25;
    word-break: break-word;
  }

  .chips {
    line-height: 0;
  }

  .chips span {
    margin: 0 8px 8px 0;
  }

  .cta-card {
    margin-top: 20px;
    background: ${C.white};
    border: 2px solid #CBD5E1;
    border-radius: 22px;
    padding: 24px 24px 20px;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
  }

  .cta-top {
    display: table;
    width: 100%;
  }

  .cta-qr-cell,
  .cta-copy-cell {
    display: table-cell;
    vertical-align: middle;
  }

  .cta-qr-cell {
    width: 210px;
    padding-right: 20px;
  }

  .qr-box {
    width: 196px;
    height: 196px;
    border: 2px solid ${C.line};
    border-radius: 16px;
    padding: 8px;
    background: ${C.white};
  }

  .qr-box img {
    width: 100%;
    height: 100%;
    display: block;
  }

  .apply-url {
    margin-top: 12px;
    font-family: 'Plus Jakarta Sans', ${BADGE_FONT};
    font-size: 34px;
    font-weight: 800;
    color: #1D4ED8;
    line-height: 1.15;
  }

  .apply-hint {
    margin-top: 8px;
    font-size: 18px;
    font-weight: 600;
    color: ${C.muted};
    line-height: 1.35;
  }

  .scan-steps {
    margin-top: 12px;
  }

  .scan-step {
    font-size: 17px;
    font-weight: 600;
    color: ${C.inkSoft};
    line-height: 1.5;
    padding-left: 4px;
  }

  .contact-heading {
    margin-top: 22px;
    margin-bottom: 12px;
    font-family: ${BADGE_FONT};
    font-size: 20px;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: ${C.ink};
    text-align: center;
  }

  .contact-grid {
    display: grid;
    grid-template-columns: repeat(${contactCols}, 1fr);
    gap: 12px;
  }

  .contact-block {
    background: #FFFFFF;
    border: 2px solid #1E293B;
    border-radius: 14px;
    padding: 16px 14px 14px;
    text-align: center;
    min-height: 118px;
  }

  .contact-block-icon {
    font-size: 28px;
    line-height: 1;
    margin-bottom: 6px;
  }

  .contact-block-label {
    font-family: ${BADGE_FONT};
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #475569;
    margin-bottom: 8px;
  }

  .contact-block-value {
    font-family: ${BADGE_FONT};
    font-size: 26px;
    font-weight: 800;
    color: #000000;
    line-height: 1.3;
    word-break: break-word;
  }
</style>

<div class="shell">
  <div class="top-card">
    <div class="brand-row">
      <div class="brand-row-inner">
        <div class="brand-logo-cell">
          ${
            absoluteLogoUrl
              ? `<img src="${escapeHtml(absoluteLogoUrl)}" alt="" class="brand-logo" crossorigin="anonymous" />`
              : `<div class="brand-logo-fallback">${escapeHtml(
                  (branding.appName || 'CO')
                    .split(/\s+/)
                    .map(w => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()
                )}</div>`
          }
        </div>
        <div class="brand-copy-cell">
          <div class="brand-name">${escapeHtml(branding.appName || 'Company')}</div>
          ${branding.tagline ? `<div class="brand-tagline">${escapeHtml(truncate(branding.tagline, 56))}</div>` : ''}
        </div>
      </div>
    </div>
    ${industry ? `<div class="industry-wrap">${industryPill}</div>` : ''}
  </div>

  <div class="badge-wrap">${hiringBadge}</div>

  <div class="title-card">
    <div class="job-title">${escapeHtml(title)}</div>
    <div class="job-meta">${positions} Opening${positions === 1 ? '' : 's'} · Apply Today</div>
  </div>

  <div class="section">
    <div class="section-title">Role Overview</div>
    <div class="info-grid">${infoTiles}</div>
  </div>

  ${skillsHtml}

  <div class="cta-card">
    <div class="cta-top">
      <div class="cta-qr-cell">
        <div class="qr-box">
          <img src="${escapeHtml(qrDataUrl)}" alt="QR" crossorigin="anonymous" />
        </div>
      </div>
      <div class="cta-copy-cell">
        ${applyPill}
        <div class="apply-url">${escapeHtml(HIRING_REGISTER_DISPLAY)}</div>
        <div class="apply-hint">Scan QR or visit the link above to register &amp; apply online.</div>
        <div class="scan-steps">
          <div class="scan-step">① Scan QR with your phone camera</div>
          <div class="scan-step">② Create your candidate profile</div>
          <div class="scan-step">③ Apply for this role instantly</div>
        </div>
      </div>
    </div>
    ${
      `<div class="contact-heading">Contact Us</div><div class="contact-grid">${contactBlocks}</div>`
    }
  </div>
</div>
</div>
`;
}

async function ensurePosterFonts() {
  if (!fontsPromise) {
    fontsPromise = (async () => {
      if (!document.getElementById('job-poster-fonts')) {
        const link = document.createElement('link');
        link.id = 'job-poster-fonts';
        link.rel = 'stylesheet';
        link.href = POSTER_FONT_URL;
        document.head.appendChild(link);
      }
      await document.fonts?.ready;
      await new Promise(r => setTimeout(r, 300));
    })();
  }
  return fontsPromise;
}

async function buildQrDataUrl(registerUrl) {
  return QRCode.toDataURL(registerUrl, {
    width: 320,
    margin: 1,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light: '#FFFFFF' }
  });
}

export async function renderJobPosterImage({ job, branding, absoluteLogoUrl }) {
  await ensurePosterFonts();

  const registerUrl = buildRegisterUrl(job?.id);
  const qrDataUrl = await buildQrDataUrl(registerUrl);

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText =
    'position:fixed;left:-12000px;top:0;z-index:-1;pointer-events:none;overflow:hidden;';
  host.innerHTML = buildJobPosterHtml({
    job,
    branding,
    absoluteLogoUrl,
    qrDataUrl
  });
  document.body.appendChild(host);

  const root = host.querySelector('#job-poster-root');
  if (!root) {
    document.body.removeChild(host);
    throw new Error('Poster markup failed to render');
  }

  const images = [...root.querySelectorAll('img')];
  await Promise.all(
    images.map(
      img =>
        new Promise(resolve => {
          if (img.complete) resolve();
          else {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          }
        })
    )
  );

  try {
    const canvas = await html2canvas(root, {
      width: WHATSAPP_STATUS_SIZE.width,
      height: WHATSAPP_STATUS_SIZE.height,
      scale: 1,
      useCORS: true,
      allowTaint: false,
      backgroundColor: C.bg,
      logging: false
    });
    return canvas.toDataURL('image/png');
  } finally {
    document.body.removeChild(host);
  }
}

export function buildPosterCaption(job, branding) {
  const title = job.title || 'Open Position';
  const location = job.location || '';
  const employmentType = formatEmploymentType(job.employmentType);
  const salary = formatSalaryRange(job.budgetFrom, job.budgetTo);
  const registerUrl = buildRegisterUrl(job?.id);
  const lines = [
    `🟢 WE ARE HIRING — ${title}`,
    branding.natureOfBusiness ? `Industry: ${branding.natureOfBusiness}` : '',
    location ? `📍 Location: ${location}` : '',
    salary !== 'As per experience' ? `💰 CTC: ${salary}` : '',
    employmentType ? `🕐 Employment: ${employmentType}` : '',
    job.noticePeriod ? `📅 Notice: ${job.noticePeriod}` : '',
    '',
    `👉 Register & apply: ${registerUrl}`,
    `Or visit: ${HIRING_REGISTER_DISPLAY}`
  ].filter(Boolean);
  if (branding.phone) lines.push(`📱 ${branding.phone}`);
  if (branding.email) lines.push(`✉ ${branding.email}`);
  if (branding.website) lines.push(`🌐 ${displayWebsite(branding.website)}`);
  return lines.join('\n');
}
